import { combineReducers } from 'redux';
import colors from './colors';
import {
  editorSettingsBehaviour,
  editorSettingsDesign,
  editorSettingsLayout,
  editorSettingsTexts,
} from './editorSettings';
import { visibilitySettings } from './visibilitySettings';
import { viewMode } from './viewMode';
import { shardId } from './shardId';
import { correlationId } from './correlationId';
import { businessChatRoomId } from './businessChatRoomId';
import { isChatSdkInit } from './isChatSdkInit';
import { coreChatToken } from './coreChatToken';
import { isBranded } from './isBranded';
import wix from '../services/wix';
import { AVATARS } from '../constants/avatars';
import _get from 'lodash/get';
import _findKey from 'lodash/findKey';
import utils from '../services/utils';
import { ON_NEW_MESSAGE } from '@wix/inbox-common';
import { EDITOR_VIEW_MODE } from '../constants/editor-view-mode';
import { availabilityStatus } from './availabilityStatus';
import { socialChatEnabled } from './socialChatEnabled';
import { businessChatEnabled } from './businessChatEnabled';
import { showSocialListInEditor } from './showSocialListInEditor';
import { widgetExpanded } from './widgetExpanded';
import { currentChatroom } from './currentChatroom';
import { fonts } from './fonts';
import { allowInput } from './allowInput';
import { hasMessages } from './hasMessages';
import { wasLeadCaptureFormFilled } from './wasLeadCaptureFormFilled';
import { isLcfMandatory } from './isLcfMandatory';
import { everExpanded } from './everExpanded';
import { DEFAULT_WIDGET_FONT } from '../constants/widget-default-font';
import { unreadCount } from './unreadCount';
import { backgroundPattern } from './backgroundPattern';
import { chatOnPage } from './chatOnPage';

const reducer = combineReducers({
  shardId,
  correlationId,
  businessChatRoomId,
  isChatSdkInit,
  coreChatToken,
  availabilityStatus,
  isBranded,
  colors,
  fonts,
  editorSettingsDesign,
  editorSettingsLayout,
  editorSettingsTexts,
  editorSettingsBehaviour,
  visibilitySettings,
  viewMode,
  socialChatEnabled,
  businessChatEnabled,
  showSocialListInEditor,
  widgetExpanded,
  currentChatroom,
  allowInput,
  hasMessages,
  isLcfMandatory,
  wasLeadCaptureFormFilled,
  everExpanded,
  unreadCount,
  backgroundPattern,
  chatOnPage,
  i18nData: (state = {}) => state,
  locale: (state = 'en') => state,
  deviceType: (state = 'desktop') => state,
  experimentsRaw: (state = {}) => state,
  experiments: (state = {}) => state,
  chatInitResult: (state = {}) => state,
  widgetMode: (state = '') => state,
  disableAutoMessages: (state = false) => state,
  localizedUserText: (state = {}) => state,
  isPrimaryLanguage: (state = true) => state,
  host: (state = 'Wix') => state,
});

export default reducer;

export type AppState = ReturnType<typeof reducer>;

export const isExperimentOn = ({ experiments }, specName) =>
  !!experiments[specName];

export function shouldShowChat(state) {
  const {
    editorVisibility,
    publicVisibility,
    chatOnboarded,
  } = state.visibilitySettings;

  if (wix.isInTemplate()) {
    return true;
  }

  if (
    [EDITOR_VIEW_MODE.EDITOR, EDITOR_VIEW_MODE.PREVIEW].indexOf(
      state.viewMode,
    ) !== -1
  ) {
    return editorVisibility;
  }

  return state.chatOnPage && publicVisibility && chatOnboarded;
}

export function primaryColorOrDefault(state) {
  const { primaryColor, addPanelColor, color8, color1 } = state.colors;
  return wix.colorationDecision({
    userChoice: primaryColor,
    addPanelColor,
    defaultColor: color8,
    color1,
  });
}

export function textColorOrDefault(state) {
  const { textColor, addPanelTextColor, color5, color1 } = state.colors;
  return wix.colorationDecision({
    userChoice: textColor,
    addPanelColor: addPanelTextColor,
    defaultColor: color5,
    color1,
  });
}

export function editorSettingsAvatar(state) {
  const { customImg, selectedImageOption } = state.editorSettingsDesign;
  const relativeUri = _get(customImg, '0.relativeUri');
  const imgUrl = wix.getResizedImgUrl(relativeUri, 180, 180);

  if (selectedImageOption === AVATARS.IMG.key) {
    return relativeUri ? { ...AVATARS.IMG.prop, imgUrl } : AVATARS.NONE.prop;
  }

  const key = _findKey(AVATARS, { key: selectedImageOption });
  return key ? AVATARS[key].prop : AVATARS.DEFAULT.prop;
}

export const getLocalizedTexts = state => {
  const editorSettings2userLocalized = {
    onlineTitle: 'header_online_title',
    onlineMessage: 'header_online_message',
    offlineTitle: 'header_offline_title',
    offlineMessage: 'header_offline_message',
    offlineChatMessage: 'header_offline_chat-message',
  };

  const getForCurrentLanguage = key =>
    (state.isPrimaryLanguage
      ? state.editorSettingsTexts[key]
      : state.localizedUserText[editorSettings2userLocalized[key]]) ||
    state.editorSettingsTexts[key];

  return {
    onlineTitle: getForCurrentLanguage('onlineTitle'),
    onlineMessage: getForCurrentLanguage('onlineMessage'),
    offlineTitle: getForCurrentLanguage('offlineTitle'),
    offlineMessage: getForCurrentLanguage('offlineMessage'),
    offlineChatMessage: getForCurrentLanguage('offlineChatMessage'),
  };
};

export const headerFontFamily = state => {
  const {
    fonts: { header = DEFAULT_WIDGET_FONT },
  } = state;
  return header;
};

export const messagesFontFamily = state => {
  const {
    fonts: { messages = DEFAULT_WIDGET_FONT },
  } = state;
  return messages;
};

export const headerTextColorOrDefault = ({ colors: { headerTextColor } }) =>
  headerTextColor || '#ffffff';
export const selectedRadiusOption = ({
  editorSettingsDesign: { selectedRadiusOption: _selectedRadiusOption },
}) => _selectedRadiusOption || '8';

export function theme(state): Theme {
  const primaryColor = primaryColorOrDefault(state);
  const textColor = textColorOrDefault(state);
  const headerTextColor = headerTextColorOrDefault(state);
  const _editorSettingsDesign = state.editorSettingsDesign;
  const _editorSettingsLayout = state.editorSettingsLayout;
  const _backgroundPattern = state.backgroundPattern;

  const _selectedRadiusOption = _editorSettingsDesign.selectedRadiusOption;
  const collapsedAsRounded = _editorSettingsLayout.collapsedAsRounded;
  const roomBackground = utils.getRgbA(primaryColor, 0.1);
  const messageBackground = utils.getRgbA(primaryColor, 0.3);
  const unreadAnchorStrokeColor = utils.getRgbA(primaryColor, 0.5);
  const unreadAnchorColor = utils.getRgbA(primaryColor, 0.7);
  const timestampColor = utils.getRgbA(textColor, 0.45);
  const searchBoxColor = utils.getRgbA(textColor, 0.07);
  const emojiPanelBackgroundColor = utils.getRgbA(textColor, 0.02);
  const borderRadius = !isNaN(_selectedRadiusOption)
    ? Number(_selectedRadiusOption)
    : undefined;
  const headerFont = headerFontFamily(state);
  const messagesFont = messagesFontFamily(state);
  const DEFAULT_BACKGROUND_BUTTON_LIGHT = '#FFFFFF';
  const DEFAULT_BACKGROUND_BUTTON_DISABLE = '#969696';

  return {
    widget: {
      borderRadius,
    },
    room: {
      button: {
        background: primaryColor,
        borderRadius,
        textColor: headerTextColor,
      },
      unreadAnchor: {
        color: unreadAnchorColor,
        strokeColor: unreadAnchorStrokeColor,
      },
      emojiPanel: {
        backgroundColor: emojiPanelBackgroundColor,
        borderColor: primaryColor,
      },
      message: {
        background: messageBackground,
        borderRadius,
        textColor,
        button: {
          background: '#6072ff',
        },
      },
      background: roomBackground,
      pattern: {
        asset: _backgroundPattern.asset,
        backgroundSize: _backgroundPattern.backgroundSize,
      },
      timestamp: {
        color: timestampColor,
      },
      typingIndication: {
        pointsColor: primaryColor,
      },
      fontFamily: messagesFont,
    },
    header: {
      background: primaryColor,
      textColor: headerTextColor,
      collapsedAsRounded,
      fontFamily: headerFont,
    },
    input: {
      color: textColor,
      opacityOnFocus: true,
    },
    chatroomList: {
      fontFamily: messagesFont,
    },
    messageNotification: {
      borderRadius,
      fontFamily: messagesFont,
      callToActionColor: primaryColor,
      textColor: state.colors.color5,
    },
    searchBox: {
      borderRadius,
      backgroundColor: searchBoxColor,
      color: textColor,
    },
    button: {
      borderRadius,
      enable: {
        backgroundColor: primaryColor,
        color: headerTextColor,
        borderColor: undefined,
      },
      light: {
        backgroundColor: DEFAULT_BACKGROUND_BUTTON_LIGHT,
        color: primaryColor,
        borderColor: primaryColor,
      },
      disable: {
        backgroundColor: DEFAULT_BACKGROUND_BUTTON_DISABLE,
        color: headerTextColor,
        borderColor: undefined,
      },
    },
  };
}

export const isMessageNotificationEnabled = state => {
  return (
    isExperimentOn(state, 'specs.chat.MessageNotification') &&
    state.editorSettingsBehaviour.onNewMessage ===
      ON_NEW_MESSAGE.SHOW_NOTIFICATION
  );
};

export interface ThemeButton {
  backgroundColor: string;
  color: string;
  borderColor: string | undefined;
}

export interface Theme {
  widget: {
    borderRadius: number | undefined;
  };
  room: {
    button: {
      background: string;
      borderRadius: number | undefined;
      textColor: string;
    };
    unreadAnchor: {
      color: string;
      strokeColor: string;
    };
    emojiPanel: {
      backgroundColor: string;
      borderColor: string;
    };
    message: {
      background: string;
      borderRadius: number | undefined;
      textColor: string;
      button: {
        background: string;
      };
    };
    background: string;
    pattern: {
      asset: string;
      backgroundSize: string;
    };
    timestamp: {
      color: string;
    };
    typingIndication: {
      pointsColor: string;
    };
    fontFamily: string;
  };
  header: {
    background: string;
    textColor: string;
    collapsedAsRounded: string;
    fontFamily: string;
  };
  input: {
    color: string;
    opacityOnFocus: boolean;
  };
  chatroomList: {
    fontFamily: string;
  };
  messageNotification: {
    borderRadius: number | undefined;
    fontFamily: string;
    callToActionColor: string;
    textColor: string;
  };
  searchBox: {
    borderRadius: number | undefined;
    backgroundColor: string;
    color: string;
  };
  button: {
    borderRadius: number | undefined;
    enable: ThemeButton;
    light: ThemeButton;
    disable: ThemeButton;
  };
}
